import { Link, useSearchParams } from 'react-router-dom';
import { CollapsibleSection } from '../components/goals/CollapsibleSection';
import { EmptyState } from '../components/EmptyState';
import { LinkSkillIcon } from '../components/links/LinkSkillIcon';
import { ServerTabStrip, type ServerTabItem } from '../components/links/ServerTabStrip';
import { useLinkProgress, useUpsertLinkProgress } from '../lib/linkProgress';
import {
  accountLinkTotals,
  BRACKET_ORDER,
  formatSharedGroupName,
  rankOfBracket,
  shareGroupTotals,
  type Bracket,
} from '../lib/links';
import { groupClassesByBranch, useClasses, useWorlds, type ClassDto } from '../lib/reference';
import { useAccounts } from '../lib/roster';

const BRACKET_LABEL: Record<Bracket, string> = { NONE: 'None', L70: '70+', L120: '120+', L210: '210+' };

const CHARACTERS_BADGE_CLASSES =
  'inline-flex items-center rounded-full bg-accent-dim px-2.5 py-0.5 text-xs font-semibold text-accent-strong transition-colors hover:bg-accent hover:text-accent-contrast';

function LinkClassRow({
  klass,
  accountId,
  derivedBracket,
  effectiveBracket,
  derivedFromCharacterName,
  derivedFromLevel,
  groupTotal,
  pending,
}: {
  klass: ClassDto;
  accountId: number;
  derivedBracket: string;
  effectiveBracket: string;
  derivedFromCharacterName: string | null;
  derivedFromLevel: number | null;
  groupTotal: { totalLevels: number; maxLevels: number } | null;
  pending: boolean;
}) {
  const upsert = useUpsertLinkProgress(accountId);
  const floorRank = rankOfBracket(derivedBracket);

  return (
    <li className="card-hover flex flex-wrap items-center gap-3 rounded-lg border border-border bg-bg-elevated-hover px-3 py-2">
      {klass.linkSkill && <LinkSkillIcon skillName={klass.linkSkill.name} />}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text">{klass.name}</p>
        <p className="truncate text-xs text-text-faint">
          {klass.linkSkill?.name ?? 'No link skill'}
          {klass.linkSkill?.effectMax ? ` — ${klass.linkSkill.effectMax}` : ''}
        </p>
        {derivedFromCharacterName && (
          <p className="text-xs italic text-text-muted">
            from roster: {derivedFromCharacterName} Lv {derivedFromLevel}
          </p>
        )}
        {groupTotal && (
          <p className="text-xs tabular-nums text-text-faint">
            {klass.linkSkill?.sharedGroup ? formatSharedGroupName(klass.linkSkill.sharedGroup) : 'Shared'} group:{' '}
            {groupTotal.totalLevels}/{groupTotal.maxLevels}
          </p>
        )}
      </div>
      <select
        aria-label={`Link bracket for ${klass.name}`}
        value={effectiveBracket}
        disabled={pending}
        onChange={(e) => upsert.mutate({ classSlug: klass.slug, bracket: e.target.value })}
        className="min-h-tap rounded-lg border border-border-strong bg-bg-elevated px-3 text-sm text-text focus:border-accent focus:outline-none disabled:opacity-50"
      >
        {BRACKET_ORDER.map((bracket) => (
          <option key={bracket} value={bracket} disabled={rankOfBracket(bracket) < floorRank}>
            {BRACKET_LABEL[bracket]}
            {rankOfBracket(bracket) < floorRank ? ' (below roster floor)' : ''}
          </option>
        ))}
      </select>
    </li>
  );
}

/**
 * `/links` — the account-wide link-skill tracker (WP7 spec; REV3 redesign: server tabs, icons,
 * attained/unattained split). Judgment call: branches render as `CollapsibleSection` accordions at
 * every viewport (not a separate flat desktop table) — the same component the character detail
 * page uses, which keeps one interaction pattern for "grouped, collapsible progression data"
 * across the whole goals module rather than maintaining two layouts.
 */
export function Links() {
  const accountsQuery = useAccounts();
  const classesQuery = useClasses();
  const worldsQuery = useWorlds();
  const [searchParams, setSearchParams] = useSearchParams();

  const accounts = accountsQuery.data ?? [];
  const worlds = worldsQuery.data ?? [];

  const accountParam = searchParams.get('account');
  const requestedAccountId = accountParam !== null ? Number(accountParam) : null;
  const validAccountIds = new Set(accounts.map((a) => a.id));
  const effectiveAccountId =
    requestedAccountId !== null && validAccountIds.has(requestedAccountId)
      ? requestedAccountId
      : (accounts[0]?.id ?? null);

  const selectTab = (id: number) => {
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      next.set('account', String(id));
      return next;
    });
  };

  const linkProgressQuery = useLinkProgress(effectiveAccountId);
  const upsert = useUpsertLinkProgress(effectiveAccountId ?? -1);

  if (accountsQuery.isPending || classesQuery.isPending) {
    return <p className="text-sm text-text-muted">Loading link skills…</p>;
  }
  if (accountsQuery.isError || classesQuery.isError) {
    return (
      <p role="alert" className="text-sm text-danger">
        Could not load link skills. Please retry in a moment.
      </p>
    );
  }
  if (accounts.length === 0) {
    return (
      <EmptyState
        title="No accounts yet"
        note="Add your main under Characters and let's track your link skills."
        actionTo="/characters"
        actionLabel="Go to Characters"
      />
    );
  }

  const classes = classesQuery.data ?? [];
  const progress = linkProgressQuery.data ?? [];
  const progressBySlug = new Map(progress.map((p) => [p.classSlug, p]));
  const totals = accountLinkTotals(classes, progress);
  const groups = new Map(shareGroupTotals(classes, progress).map((g) => [g.sharedGroup, g]));
  const pendingSlug = upsert.isPending ? upsert.variables?.classSlug : null;

  const attainedClasses = classes.filter(
    (k) => (progressBySlug.get(k.slug)?.effectiveBracket ?? 'NONE') !== 'NONE',
  );
  const unattainedClasses = classes.filter(
    (k) => (progressBySlug.get(k.slug)?.effectiveBracket ?? 'NONE') === 'NONE',
  );
  const attainedBranches = groupClassesByBranch(attainedClasses);

  // Tab list = every account, labeled by its world (falling back to the raw slug if the worlds
  // reference catalog hasn't loaded yet), with the account's own label as microtext — that keeps
  // two same-world accounts visually distinct without any extra collision-detection logic.
  const tabs: ServerTabItem[] = accounts.map((a) => ({
    id: a.id,
    label: worlds.find((w) => w.slug === a.worldSlug)?.name ?? a.worldSlug,
    microtext: a.label,
  }));

  const renderRow = (klass: ClassDto) => {
    const p = progressBySlug.get(klass.slug);
    const sharedGroup = klass.linkSkill?.sharedGroup ?? null;
    return (
      <LinkClassRow
        key={klass.slug}
        klass={klass}
        accountId={effectiveAccountId ?? -1}
        derivedBracket={p?.derivedBracket ?? 'NONE'}
        effectiveBracket={p?.effectiveBracket ?? 'NONE'}
        derivedFromCharacterName={p?.derivedFromCharacterName ?? null}
        derivedFromLevel={p?.derivedFromLevel ?? null}
        groupTotal={sharedGroup ? (groups.get(sharedGroup) ?? null) : null}
        pending={pendingSlug === klass.slug}
      />
    );
  };

  return (
    <div className="stagger-children space-y-5">
      <div>
        <h1 className="font-display text-xl font-semibold text-text">Link Skills</h1>
        <p className="mt-1 text-sm text-text-muted">Every class's link-skill milestone, account-wide.</p>
      </div>

      <ServerTabStrip tabs={tabs} selectedId={effectiveAccountId ?? -1} onSelect={selectTab} />

      {/* One integrated summary strip, not a wall of identical stat cards (docs/DESIGN.md: "no
          uniform card-grid dashboards, no oversized vanity numbers") — same shape as the boss
          matrix's income strip in BossesHeader. */}
      <div className="grid grid-cols-2 divide-x divide-border rounded-lg border border-border bg-bg-elevated sm:grid-cols-4">
        <div className="p-3">
          <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
            Maxed (210+)
          </p>
          <p className="text-lg font-semibold tabular-nums text-crystal">{totals.maxedCount}</p>
        </div>
        <div className="p-3">
          <p className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
            Link levels reached
          </p>
          <p className="text-lg font-semibold tabular-nums text-crystal">{totals.totalLevelsReached}</p>
        </div>
        {[...totals.byBranch.entries()].slice(0, 2).map(([branch, count]) => (
          <div key={branch} className="p-3">
            <p className="truncate font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
              {branch}
            </p>
            <p className="text-lg font-semibold tabular-nums text-text">{count}</p>
          </div>
        ))}
      </div>

      {linkProgressQuery.isPending ? (
        <p className="text-sm text-text-muted">Loading this account's link progress…</p>
      ) : linkProgressQuery.isError ? (
        <p role="alert" className="text-sm text-danger">
          Could not load link progress.
        </p>
      ) : (
        <div id="links-tabpanel" role="tabpanel" aria-label="Link skills for the selected server">
          <div className="space-y-5">
            {[...attainedBranches.entries()].map(([branch, branchClasses]) => (
              <CollapsibleSection key={branch} title={branch} subtitle={`${branchClasses.length} classes`}>
                <ul className="space-y-2">{branchClasses.map(renderRow)}</ul>
              </CollapsibleSection>
            ))}

            {unattainedClasses.length > 0 && (
              <CollapsibleSection
                title="Not yet attained"
                subtitle={`${unattainedClasses.length} classes`}
                defaultOpen={false}
              >
                <p className="text-sm text-text-muted">
                  Have one of these links? Add that character under{' '}
                  <Link to="/characters" className={CHARACTERS_BADGE_CLASSES}>
                    Characters
                  </Link>{' '}
                  and it'll show up here automatically.
                </p>
                <ul className="space-y-2">{unattainedClasses.map(renderRow)}</ul>
              </CollapsibleSection>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
